import {Formik, FormikHelpers, FormikProps, FormikValues} from 'formik'
import React, {FC, useEffect, useRef} from 'react'
import {Accordion} from 'react-bootstrap'
import {Suspect} from '../modules/judol/judol-list/core/_models'
import clsx from 'clsx'
import * as Yup from 'yup'
import moment from 'moment'
import ProfessionSelect from '../profession/components/ProfessionSelect'
import {SuspectRoleSelect} from '../modules/suspect-role/components/SuspectRoleSelect'

type Props = {
  isLoading: boolean
  isEditSuspect: boolean
  suspects: Suspect[]
  isTerlaporTersangka: boolean
  setSuspect: React.Dispatch<React.SetStateAction<Suspect[]>>
  setEditSuspect: React.Dispatch<React.SetStateAction<boolean>>
  handleCheckboxChange: any
  useCheckbox?: boolean
  disabled?: boolean
  useAccordion?: boolean
  showStatusId?: boolean
  resetValidation?: boolean
}

const schema = Yup.object().shape({
  name: Yup.string().required('Nama wajib diisi'),
  nik: Yup.string()
    .matches(/^[0-9]*$/, 'NIK harus berupa angka')
    .length(16, 'NIK harus 16 karakter')
    .notRequired(),
  gender: Yup.string()
    .oneOf(['1', '2'], 'Jenis Kelamin wajib dipilih')
    .required('Jenis Kelamin wajib dipilih'),
})

const ReportSuspect: FC<Props> = ({
  isLoading,
  isEditSuspect,
  isTerlaporTersangka,
  setEditSuspect,
  setSuspect,
  suspects,
  handleCheckboxChange,
  useCheckbox = true,
  disabled = false,
  useAccordion = true,
  showStatusId = false,
  resetValidation = false,
}) => {
  const nameRef = useRef<HTMLInputElement | null>(null)
  const formRef = useRef<FormikProps<Suspect>>(null)

  useEffect(() => {
    formRef.current?.setTouched({})
  }, [resetValidation])

  const FormComp = () => {
    return (
      <Formik
        onSubmit={(values: FormikValues, helper: FormikHelpers<Suspect>) => {
          if (isEditSuspect) {
            const idx = suspects.findIndex((q) => q.key === values.key)

            helper.resetForm({
              values: {
                address: '',
                dob: '',
                gender: 0,
                id: '',
                key: '',
                name: '',
                nik: '',
                pob: '',
                jobId: undefined,
                age: 0,
                roleId: undefined,
                statusId: undefined,
              },
            })

            if (idx >= 0) {
              console.log(suspects)
              setSuspect([...suspects.filter((_, i) => i !== idx), values])
              setEditSuspect(false)
            }

            // nameRef.current?.focus()

            return
          }

          setSuspect([...suspects, values])
          helper.setSubmitting(false)
          helper.resetForm({
            values: {
              address: '',
              dob: '',
              gender: 0,
              id: '',
              key: '',
              name: '',
              nik: '',
              pob: '',
              jobId: undefined,
              age: 0,
              roleId: undefined,
              statusId: undefined,
            },
          })

          nameRef.current?.focus()
        }}
        initialValues={
          {
            address: '',
            dob: '',
            gender: 0,
            id: undefined,
            key: undefined,
            name: '',
            nik: '',
            pob: '',
            jobId: undefined,
            age: 0,
            roleId: undefined,
            statusId: undefined,
          } as Suspect
        }
        validationSchema={schema}
        innerRef={formRef}
      >
        {(formik) => (
          <div className='container-fluid pt-1'>
            <div>
              <label className='form-section-title'>
                <i className='fa-regular fa-file-lines'></i> Form Data Tersangka
              </label>
              <div className='row mb-2'>
                {useCheckbox && (
                  <div className='form-check'>
                    <input
                      type='checkbox'
                      id='terlaporTersangka'
                      className='form-check-input'
                      checked={isTerlaporTersangka}
                      onChange={handleCheckboxChange}
                      disabled={disabled}
                    />
                    <label htmlFor='terlaporTersangka' className='form-check-label mb-3'>
                      Tersangka adalah terlapor
                    </label>
                  </div>
                )}
                <div className='col-md-6 mb-3'>
                  <input type='hidden' value={formik.getFieldProps('key').value} />
                  <label htmlFor='name' className='form-label required'>
                    Nama
                  </label>
                  <input
                    ref={nameRef}
                    type='text'
                    id='name'
                    className={clsx(
                      'form-control mb-3 mb-lg-0',
                      {
                        'is-invalid': formik.touched.name && formik.errors.name,
                      },
                      {
                        'is-valid': formik.touched.name && !formik.errors.name,
                      }
                    )}
                    {...formik.getFieldProps('name')}
                    disabled={disabled}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.name}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='mb-3 col-md-6'>
                  <div className='fv-row d-flex flex-column mb-7'>
                    <label className='form-label required'>Jenis Kelamin</label>
                    <div className='form-check-inline mt-3'>
                      <div className='form-check form-check-inline'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='suspectGenderOptions'
                          id='male'
                          value='1'
                          checked={formik.values.gender === 1}
                          onChange={(e) => formik.setFieldValue('gender', 1)}
                          disabled={disabled}
                        />
                        <label htmlFor='male'>Laki-Laki</label>
                      </div>
                      <div className='form-check form-check-inline'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='suspectGenderOptions'
                          id='female'
                          value='2'
                          checked={formik.values.gender === 2}
                          onChange={(e) => formik.setFieldValue('gender', 2)}
                          disabled={disabled}
                        />
                        <label htmlFor='female'>Perempuan</label>
                      </div>
                    </div>
                  </div>
                  {formik.touched.gender && formik.errors.gender && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.gender}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='col-md-6  mb-6'>
                  <label htmlFor='nik' className='form-label'>
                    NIK
                  </label>
                  <input
                    type='text'
                    className={clsx(
                      'form-control',
                      {
                        'is-invalid': formik.touched.nik && formik.errors.nik,
                      },
                      {
                        'is-valid': formik.touched.nik && !formik.errors.nik,
                      }
                    )}
                    placeholder='NIK'
                    {...formik.getFieldProps('nik')}
                    maxLength={16}
                    disabled={disabled}
                  />
                  {formik.touched.nik && formik.errors.nik && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.nik}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='col-md-6 mb-3'>
                  <label htmlFor='pob' className='form-label'>
                    Tempat Lahir
                  </label>
                  <input
                    type='text'
                    className={clsx(
                      'form-control',
                      {
                        'is-invalid': formik.touched.pob && formik.errors.pob,
                      },
                      {
                        'is-valid': formik.touched.pob && !formik.errors.pob,
                      }
                    )}
                    placeholder='Masukkan tempat lahir'
                    {...formik.getFieldProps('pob')}
                    disabled={disabled}
                  />
                  {formik.touched.pob && formik.errors.pob && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.pob}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='col-md-4'>
                  <div className='fv-row d-flex flex-column mb-3'>
                    <label className='form-label' htmlFor='dob'>
                      Tanggal Lahir
                    </label>
                    <input
                      type='date'
                      className={clsx(
                        'form-control',
                        {
                          'is-invalid': formik.touched.dob && formik.errors.dob,
                        },
                        {
                          'is-valid': formik.touched.dob && !formik.errors.dob,
                        }
                      )}
                      {...formik.getFieldProps('dob')}
                      onChange={(e) => {
                        const dob = e.target.value
                        if (dob) {
                          const age = moment().diff(dob, 'years')
                          formik.setFieldValue('dob', dob)
                          formik.setFieldValue('age', age)
                          return
                        }

                        formik.setFieldValue('dob', dob)
                      }}
                      disabled={disabled}
                    />
                    {formik.touched.dob && formik.errors.dob && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.dob}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='col-md-2'>
                  <div className='fv-row d-flex flex-column mb-2'>
                    <label className='form-label' htmlFor='age'>
                      Umur
                    </label>
                    <input
                      type='text'
                      className={'form-control'}
                      {...formik.getFieldProps('age')}
                      placeholder='Umur'
                      disabled={formik.getFieldProps('dob').value || disabled}
                    />
                  </div>
                </div>
                {showStatusId && (
                  <div className='mb-3 col-md-3'>
                    <div className='fv-row d-flex flex-column mb-7'>
                      <label className='form-label'>Status</label>
                      <div className='form-check-inline mt-3'>
                        {[
                          {name: 'Sidik', value: 1},
                          {name: 'Rehab', value: 2},
                        ].map((m, i) => (
                          <div className='form-check form-check-inline' key={i}>
                            <input
                              className='form-check-input'
                              type='radio'
                              name='statusId'
                              id={'statusId-' + m.value}
                              value={m.value + ''}
                              checked={formik.values.statusId === m.value}
                              onChange={(e) => formik.setFieldValue('statusId', m.value)}
                              disabled={disabled}
                            />
                            <label htmlFor={'statusId-' + m.value}>{m.name}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    {formik.touched.gender && formik.errors.gender && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.gender}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className='col-md-6'>
                  <div className='fv-row d-flex flex-column mb-7'>
                    <label className='form-label'>Profesi</label>
                    <ProfessionSelect
                      isMulti={false}
                      form={formik}
                      field={formik.getFieldProps('jobId')}
                      initialOptions={
                        formik.values.jobId
                          ? [
                              {
                                value: formik.values.jobId,
                                label: formik.values.jobName,
                              },
                            ]
                          : []
                      }
                      disabled={disabled}
                    />
                  </div>
                  {formik.touched.jobId && formik.errors.jobId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.jobId}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='col-md-6'>
                  <div className='fv-row d-flex flex-column mb-7'>
                    <label className='fs-6 mb-2'>Peran</label>
                    <SuspectRoleSelect
                      form={formik}
                      field={formik.getFieldProps('roleId')}
                      disabled={disabled}
                    />
                  </div>
                  {formik.touched.roleId && formik.errors.roleId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.roleId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row'>
                <div className='col-md-12'>
                  <label className='form-label' htmlFor='address'>
                    Alamat
                  </label>
                  <textarea
                    rows={1}
                    className={clsx(
                      'form-control',
                      {
                        'is-invalid': formik.touched.address && formik.errors.address,
                      },
                      {
                        'is-valid': formik.touched.address && !formik.errors.address,
                      }
                    )}
                    {...formik.getFieldProps('address')}
                    disabled={disabled}
                  ></textarea>
                  {formik.touched.address && formik.errors.address && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.address}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='d-flex justify-content-center mt-7'>
                {isEditSuspect && (
                  <button
                    type='button'
                    className='btn me-3 btn-sm btn btn-outline btn-outline-danger'
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      formik.setValues({
                        address: '',
                        dob: '',
                        gender: 0,
                        id: undefined,
                        key: undefined,
                        name: '',
                        nik: '',
                        pob: '',
                        jobId: undefined,
                        age: 0,
                      })
                      setEditSuspect(false)
                    }}
                  >
                    Batal
                  </button>
                )}

                {!disabled && (
                  <button
                    className='btn btn-sm btn-primary'
                    type='button'
                    onClick={(e) => {
                      formik.submitForm()
                    }}
                  >
                    {isEditSuspect ? 'Update Data' : 'Tambah Tersangka'}
                  </button>
                )}
              </div>
            </div>

            <hr />
            <label className='form-section-title mt-5'>
              <i className='fa-regular fa-file-lines'></i> List Data Tersangka
            </label>

            <div className='row'>
              <div className='col-md-12 mt-3'>
                <table
                  id='kt_table_student'
                  className='table table-striped table-rounded dataTable align-middle table-row-dashed no-footer gs-7 fs-6 gy-5'
                >
                  <thead>
                    <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                      <th>No</th>
                      <th>Nama</th>
                      <th>NIK</th>
                      <th>Tempat Lahir</th>
                      <th>Tanggal Lahir</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 fw-bold'>
                    {suspects.length > 0 ? (
                      suspects.map((row, i) => {
                        return (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{row.name ?? '-'}</td>
                            <td>{row.nik ?? '-'}</td>
                            <td>{row.pob ?? '-'}</td>
                            <td>{row.dob ?? '-'}</td>
                            <td>
                              {
                                <div>
                                  {/* begin::Menu item */}
                                  <button
                                    type={'button'}
                                    className='btn menu-link text-primary px-3'
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      const item = suspects[i]
                                      formik.setValues({...item})
                                      setEditSuspect(true)
                                    }}
                                  >
                                    {disabled ? 'View' : 'Edit'}
                                  </button>
                                  {!disabled && (
                                    <>
                                      |
                                      <button
                                        type={'button'}
                                        className='btn menu-link text-primary px-3'
                                        data-kt-student-table-filter='delete_row'
                                        onClick={() => {
                                          setSuspect(suspects.filter((_, idx) => idx !== i))
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              }
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            {isLoading ? '' : 'No matching records found'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Formik>
    )
  }

  return useAccordion ? (
    <Accordion className='mb-3'>
      <Accordion.Item eventKey='0'>
        <Accordion.Header>Data Tersangka</Accordion.Header>
        <Accordion.Body>{FormComp()}</Accordion.Body>
      </Accordion.Item>
    </Accordion>
  ) : (
    FormComp()
  )
}

export default ReportSuspect
